import { getDiscoveryItemPath } from "@app-contexts/public/features/discovery/data/utils/discovery-utils";
import {
  CityLandingPageEventSerializer,
  PublicBaseEventSerializer,
} from "@app-contexts/public/features/event-detail/data/types/events";
import { enums } from "@shared/constants";
import cdn from "@shared/utils/urls/cdn";
import url from "@shared/utils/urls/url";
import moment from "moment-timezone";
import clip from "text-clipper";

/**
 * Creates a rich schema version of the event object returned from the getPublicEvent and getPublicEventWithSlug api calls
 * @param inputEvent event object
 * @return {JSON string}
 */
export const createRichEventSchema = (
  inputEvent: PublicBaseEventSerializer | CityLandingPageEventSerializer
): string => {
  const e = inputEvent;
  const l: typeof e.location = e.location || {};

  const getEventPath = () => getDiscoveryItemPath(e);

  const getLatitude = () => {
    return l.position?.includes(",") ? l.position.split(",")[0] : null;
  };

  // TODO: update this logic for events of type CityLandingPageEventSerializer
  const getLongitude = () => {
    return l.position?.includes(",") ? l.position.split(",")[1] : null;
  };

  const getEventOffers = () => {
    interface Offer {
      "@context": string;
      "@type": string;
      name?: string;
      url: string;
      price?: string;
      priceCurrency: string | null;
      availability: string;
      validFrom?: string;
      validThrough?: string;
    }

    let offers: Offer[];

    // events with no ticket types require special logic
    if (e.no_ticket_types || !e.ticket_types?.length) {
      const offer: Offer = {
        "@context": "http://schema.org/",
        "@type": "Offer",
        url: getEventPath(),
        price: "0",
        priceCurrency: e.venue?.currency || null,
        availability: "http://schema.org/InStock",
        validFrom: moment.tz(e.starts_on, e.timezone).format(),
        validThrough: moment.tz(e.ends_on, e.timezone).format(),
      };
      offers = [offer];
    }
    // else, map through each ticket type to create Offer schemas
    else {
      offers = e.ticket_types.map((tt) => {
        const offer: Offer = {
          "@context": "http://schema.org/",
          "@type": "Offer",
          name: tt.name,
          url: getEventPath(),
          price: tt.price,
          priceCurrency: e.venue?.currency || null,
          availability: tt.sold_out
            ? "http://schema.org/SoldOut"
            : "http://schema.org/InStock",
        };

        if (tt.sale_starts_on) {
          offer.validFrom = moment.tz(tt.sale_starts_on, e.timezone).format();
        }

        if (tt.sale_ends_on) {
          offer.validThrough = moment.tz(tt.sale_ends_on, e.timezone).format();
        }

        return offer;
      });
    }

    return offers;
  };

  const getEventPerfomers = () => {
    const performers = e.artists?.map((artist) => {
      const type =
        artist.type === enums.ARTIST_TYPE_MAP.PERSON.id
          ? "Person"
          : "PerformingGroup";
      return {
        "@type": type,
        name: artist.name,
      };
    });
    return performers;
  };

  const eventSchema = {
    "@context": "http://schema.org/",
    "@type": "Event",
    name: e.name,
    startDate: moment.tz(e.starts_on, e.timezone).format(),
    endDate: moment.tz(e.ends_on, e.timezone).format(),
    doorTime: e.opens_at
      ? moment.tz(e.opens_at, e.timezone).format()
      : moment.tz(e.starts_on, e.timezone).format(),
    description: e.description
      ? clip(e.description, e.description.length, {
          html: true,
          stripTags: true,
        })
      : `${e.name} - ${l.name}, ${l.city} ${l.province}`,
    url: getEventPath(),
    image: e.image || e.image_banner || cdn("images/default-banner.png"),
    eventAttendanceMode: e.virtual_event_type
      ? "https://schema.org/OnlineEventAttendanceMode"
      : "https://schema.org/OfflineEventAttendanceMode",
    eventStatus: "http://schema.org/EventScheduled",
    location: e.virtual_event_type
      ? {
          "@type": "VirtualLocation",
          url: getEventPath(),
        }
      : {
          "@context": "http://schema.org/",
          "@type": "Place",
          name: l.name,
          address: {
            "@context": "http://schema.org/",
            "@type": "PostalAddress",
            streetAddress: l.street_name,
            addressLocality: l.city,
            addressRegion: l.province,
            postalCode: l.postal_code,
            addressCountry: l.country,
          },
          geo: {
            "@context": "http://schema.org/",
            "@type": "GeoCoordinates",
            latitude: getLatitude(),
            longitude: getLongitude(),
          },
        },
    offers: getEventOffers(),
    organizer: {
      "@type": "Organization",
      name: e.venue?.name || null,
      url: e.venue?.web_address || url("o/{slug?}", { slug: e.venue?.slug }),
    },
    performer: getEventPerfomers(),
  };

  return JSON.stringify(eventSchema);
};
